import { IProduct } from '@shared/models/product.model';
import {
  CONSERVATIVE_A1_A3_UNIT,
  CONSERVATIVE_A5_UNIT,
  CONSERVATIVE_B2_UNIT,
  CONSERVATIVE_B6_UNIT,
  ConservativeCO2eUnit,
  Results,
} from '@shared/models/unit.model';
import { getConversionFactorValue } from './conversion-factors.helpers';
import { omit } from './object.helpers';
import { guards } from './typeguards.helpers';

/**
 * Neutral conservative factor: no uplift. Used for specific (EPD) data and any product the
 * caller has already classified as non-generic. See DEV-3411.
 */
export const NEUTRAL_CONSERVATIVE_FACTOR = 1;

/**
 * Default conservative factor for **generic** climate data that lacks an explicit Boverket
 * `ConservativeDataConversionFactor` (e.g. Ökobaudat / Nodon generic products): Boverket's
 * conservative convention uplifts generic A1–A3 by 25%. Boverket products that carry an explicit
 * factor (including an explicit `1` for "no uplift") keep their own value. See DEV-3411.
 */
export const DEFAULT_GENERIC_CONSERVATIVE_FACTOR = 1.25;

/**
 * The conservative factor to apply to a **generic** product's climate data. Returns the stored
 * Boverket `ConservativeDataConversionFactor` ({@link IProduct.characteristics}) when present —
 * including an explicit `1`, which means "no uplift" — and otherwise falls back to
 * {@link DEFAULT_GENERIC_CONSERVATIVE_FACTOR} for generic data without a stored factor.
 *
 * Callers must only invoke this for generic (non-EPD) product elements; specific EPD data must
 * be mapped to {@link NEUTRAL_CONSERVATIVE_FACTOR} by the caller (see `isGenericProductElement`).
 * See DEV-3411.
 * @param product - The generic product supplying the climate data (may be `undefined`).
 */
export const getProductConservativeFactor = (product?: IProduct): number => {
  const factor = product?.characteristics?.conservativeFactor;
  return guards.is.number(factor) && factor > 0
    ? factor
    : DEFAULT_GENERIC_CONSERVATIVE_FACTOR;
};

/**
 * Conservative carbon **delta** for a single phase value: the extra carbon added on top of the
 * standard value when the conservative view is selected, i.e. `value × (factor - 1)`. Returns
 * `0` for the neutral factor (no uplift) or a non-positive base.
 * @param value - The standard phase value (e.g. `co2e_A1-A3`).
 * @param factor - The product's conservative factor (see {@link getProductConservativeFactor}).
 */
export const getConservativeDelta = (value: number, factor: number): number =>
  value > 0 ? value * (factor - 1) : 0;

/** Pairs of conservative delta key → the base phase it is folded into. */
export const CONSERVATIVE_DELTA_PAIRS = [
  [CONSERVATIVE_A1_A3_UNIT, 'co2e_A1-A3'],
  [CONSERVATIVE_A5_UNIT, 'co2e_A5'],
  [CONSERVATIVE_B2_UNIT, 'co2e_B2'],
  [CONSERVATIVE_B6_UNIT, 'co2e_B6'],
] as const;

const CONSERVATIVE_DELTA_KEYS = CONSERVATIVE_DELTA_PAIRS.map(
  ([deltaKey]) => deltaKey,
);

export type FoldConservativeDeltasOptions = {
  /**
   * Subset of delta keys to fold. Default: both {@link CONSERVATIVE_A1_A3_UNIT} and
   * {@link CONSERVATIVE_A5_UNIT}. Boverket XML export folds A1–A3 only.
   */
  deltaKeys?: readonly ConservativeCO2eUnit[];
};

const getConservativeDeltaPairsToFold = (
  deltaKeys?: readonly ConservativeCO2eUnit[],
) => {
  if (!deltaKeys) return CONSERVATIVE_DELTA_PAIRS;
  const allowed = new Set(deltaKeys);
  return CONSERVATIVE_DELTA_PAIRS.filter(([deltaKey]) => allowed.has(deltaKey));
};

/**
 * Fold stored conservative deltas into their base phases in a flat results bucket
 * (`co2e_A1-A3 += co2e_A1-A3_c`, etc.), then drop the consumed delta keys.
 * Rows saved before DEV-3411 lack the delta keys and keep their standard values.
 * @param results - A flat {@link Results} bucket (one product row or SBEF sub-bucket).
 * @param options.deltaKeys - When set, only those deltas are folded (e.g. A1–A3 for Boverket XML).
 */
export const foldConservativeDeltasFlat = (
  results: Results,
  options: FoldConservativeDeltasOptions = {},
): Results => {
  const pairs = getConservativeDeltaPairsToFold(options.deltaKeys);
  const next = omit(results, ...pairs.map(([deltaKey]) => deltaKey));
  for (const [deltaKey, baseKey] of pairs) {
    const delta = results[deltaKey];
    if (delta === undefined) continue;
    next[baseKey] = (next[baseKey] ?? 0) + delta;
  }
  return next;
};

/**
 * Strip conservative delta keys from a flat results bucket without changing other values.
 * @param results - A flat {@link Results} bucket.
 */
export const stripConservativeDeltasFlat = (results: Results): Results =>
  omit(results, ...CONSERVATIVE_DELTA_KEYS);

/**
 * Return a copy of `results` with the conservative carbon delta keys
 * ({@link CONSERVATIVE_A1_A3_UNIT}, {@link CONSERVATIVE_A5_UNIT}) set alongside the standard
 * phases. Each delta is `phase × (conservativeFactor - 1)` so the result-scope toggle can add
 * the uplift on top of the base phase without recomputing. Keys are omitted when their delta is
 * `0`, keeping rows compressed. See DEV-3411.
 * @param results - A leaf product element's computed results.
 * @param options.conservativeFactor - The product's conservative factor (`1` = no uplift).
 */
export const withConservativeCarbon = (
  results: Results,
  { conservativeFactor }: { conservativeFactor: number },
): Results => {
  if (conservativeFactor === NEUTRAL_CONSERVATIVE_FACTOR) return results;

  const a1a3Delta = getConservativeDelta(
    getConversionFactorValue(results, 'co2e_A1-A3'),
    conservativeFactor,
  );
  const a5Delta = getConservativeDelta(
    getConversionFactorValue(results, 'co2e_A5'),
    conservativeFactor,
  );
  if (!a1a3Delta && !a5Delta) return results;

  const next: Results = { ...results };
  if (a1a3Delta) next[CONSERVATIVE_A1_A3_UNIT] = a1a3Delta;
  if (a5Delta) next[CONSERVATIVE_A5_UNIT] = a5Delta;
  return next;
};
